import { Component, inject } from '@angular/core';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatError } from '@angular/material/form-field';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { merge } from 'rxjs';
import { MatButton } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CloudFunctionsService } from '../../../services/cloud-functions.service';

import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-contact-form',
    standalone: true,
    imports: [
        MatGridListModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        FormsModule,
        ReactiveFormsModule,
        MatButton,
        MatCheckboxModule,
        MatError,
        MatSnackBarModule,
        MatProgressBarModule
    ],
    templateUrl: './contact-form.component.html',
    styleUrl: './contact-form.component.scss'
})
export class ContactFormComponent {
  private _snackBar: MatSnackBar;

  public contactForm: FormGroup;
  public errorMessage: string = '';
  public submissionMessage: string | null = null;
  public submissionStatus: 'idle' | 'pending' | 'success' | 'error' = 'idle';

  constructor(
    private fb: FormBuilder,
    private cloudFunctionsService: CloudFunctionsService
  ) {
    this._snackBar = inject(MatSnackBar);
    this.contactForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.pattern('^[0-9]*$')]],
      name: ['', [Validators.required]],
      message: ['', [Validators.required]],
      termsAccepted: [false, Validators.requiredTrue]
    });

    // Access the email FormControl after form initialization
    const emailControl = this.contactForm.get('email');

    if (emailControl) {
      merge(emailControl.statusChanges, emailControl.valueChanges)
        .pipe(takeUntilDestroyed())
        .subscribe(() => this.updateErrorMessage(emailControl));
    }
  }

  get email() {
    return this.contactForm.get('email')!;
  }
  get phone() {
    return this.contactForm.get('phone')!;
  }
  get name() {
    return this.contactForm.get('name')!;
  }
  get message() {
    return this.contactForm.get('message')!;
  }
  get termsAccepted() {
    return this.contactForm.get('termsAccepted')!;
  }

  private updateErrorMessage(control: any) {
    if (control.hasError('required')) {
      this.errorMessage = 'Bitte fülle das Feld aus';
    } else if (control.hasError('email')) {
      this.errorMessage = 'Bitte gib eine gültige E-mail-Adresse ein';
    } else {
      this.errorMessage = '';
    }
  }

  private openSnackBar(message: string, duration: number) {
    this._snackBar.open(message, 'OK', {
      duration: duration,
    });
  }

  private onSuccessfulSubmission(msg: string | undefined = undefined) {
    this.submissionMessage = null;
    this.submissionStatus = 'success';
    this.resetForm();
    if (msg) {
      this.openSnackBar(msg, 0);
    } else {
      this.openSnackBar('Vielen Dank für deine Nachricht. Wir melden uns in Kürze bei dir!', 10000);
    }
  }

  private onFailedSubmission() {
    this.submissionMessage = 'Deine Nachricht kann derzeit leider nicht gesendet werden. Bitte versuche es später erneut.';
    this.submissionStatus = 'error';
    this.openSnackBar('Es ist ein Fehler aufgetreten. Bitte versuche es später erneut.', 10000);
  }

  private resetForm() {
    this.contactForm.reset();
    Object.keys(this.contactForm.controls).forEach(key => {
      this.contactForm.get(key)!.setErrors(null);
      this.contactForm.get(key)!.setValue(null);
    });
  }

  public onSubmit() {
    if (this.contactForm.valid) {
      this.submissionStatus = 'pending';
      this.submissionMessage = 'Deine Nachricht wird gesendet...';
      console.log('Form Submitted', this.contactForm.value);
      // if (!environment.isAdminPage) {
        this.cloudFunctionsService.callFunction('create_message', this.contactForm.value).then((result) => {
          console.log('Function result:', result);
          if (result.status === 'success') {
            this.onSuccessfulSubmission();
          } else {
            this.onFailedSubmission();
          }
        })
          .catch((error) => {
            console.error('Function error:', error);
            this.onFailedSubmission();
          });
      // } else {
      //   setTimeout(() => {
      //     this.onSuccessfulSubmission('Deine Kunden können dir über dieses Formular Nachrichten senden.');
      //   }, 1000);
      // }
    } else {
      this.contactForm.markAllAsTouched();
      if (this.email.invalid) {
        this.updateErrorMessage(this.email);
      }
      this.submissionMessage = null;
    }
  }
}